import { useId, useState } from 'react';
import type { Id } from '@contracts/common';
import { INGEST_FIELD_LABELS, type BatchRowDTO } from '@contracts/ingest';
import { DateField, MonthField } from '../../../components/DateField';
import { Combobox } from '../../../components/ui/combobox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { NativeSelect } from '../../../components/ui/native-select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../lib/utils';
import { AmountField } from '../../finance/AmountField';
import { recipientOptions, useActiveUsers } from '../../tasks/RecipientField';
import { useAssistant } from '../AssistantProvider';
import { EDITOR_OF, FIELD_SUBSETS, formatValue, type Editor } from './fields';
import { RefPicker } from './RefPicker';
import type { RowError } from './useRowWriter';

const ERROR = 'text-xs font-medium text-[hsl(var(--h-error-fg))]';
const SEGMENT = 'min-h-11 rounded-md px-2.5 text-xs font-semibold transition-colors lg:min-h-0 lg:py-1';
const SEGMENT_ON = 'bg-[hsl(var(--h-card))] text-[hsl(var(--h-primary))] shadow-sm';
const SEGMENT_OFF = 'text-[hsl(var(--h-muted-foreground))] hover:text-[hsl(var(--h-card-foreground))]';
const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');

/** One field with the app's own editor for its kind of value; a typed value goes to `edit`, leaving it to `flush`. */
function Field({ field, editor, value, edit, flush }: {
  field: string; editor: Editor; value: unknown; edit: (field: string, value: unknown) => void; flush: () => void;
}) {
  const id = useId();
  const label = INGEST_FIELD_LABELS[field] ?? field;
  const set = (v: unknown) => edit(field, v);
  switch (editor.type) {
    case 'date':
      return <DateField label={label} value={str(value)} onChange={(v) => { set(v); flush(); }} />;
    case 'month':
      return <MonthField label={label} value={str(value)} onChange={(v) => { set(v); flush(); }} />;
    case 'amount':
      return <AmountField label={label} value={str(value)} onChange={set} />;
    case 'labels':
      // A partner's labels are a set of codes: shown here, changed on the partner's own page.
      return (
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="text-sm font-medium leading-none">{label}</span>
          <p className="text-sm text-[hsl(var(--h-card-foreground))]">{formatValue(field, value)}</p>
        </div>
      );
    case 'choice':
      return (
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={id}>{label}</Label>
          <NativeSelect id={id} value={str(value)} onChange={(e) => { set(e.target.value); flush(); }}>
            <option value="">—</option>
            {Object.entries(editor.choices).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </NativeSelect>
        </div>
      );
    case 'long':
      return (
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={id}>{label}</Label>
          <Textarea id={id} rows={2} value={str(value)} onChange={(e) => set(e.target.value)} />
        </div>
      );
    default:
      return (
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={id}>{label}</Label>
          <Input id={id} value={str(value)} inputMode={editor.type === 'decimal' ? 'decimal' : undefined}
            onChange={(e) => set(e.target.value)} />
        </div>
      );
  }
}

/** A task's recipient (code fills it from the parent card): the BOD list, opened from the field. */
function AssigneeField({ value, edit, flush }: { value: unknown; edit: (f: string, v: unknown) => void; flush: () => void }) {
  const { principal } = useAssistant();
  const users = useActiveUsers().data ?? [];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = typeof value === 'string' ? value : '';
  const name = users.find((u) => u.id === current)?.name;
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>Người nhận</Label>
      <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(''); }}>
        <PopoverTrigger asChild>
          <button id={id} type="button"
            className="flex h-11 w-full min-w-0 items-center rounded-md border border-input bg-background px-3 text-left text-sm shadow-sm pointer-fine:h-9">
            <span className={cn('truncate', name === undefined && 'text-[hsl(var(--h-muted-foreground))]')}>
              {name ?? 'Chọn người nhận'}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(18rem,calc(100vw-2rem))] p-2">
          <Combobox label="Người nhận" placeholder="Tìm người nhận…" query={query} onQueryChange={setQuery} autoFocus
            empty="Không có ai khớp" listClassName="max-h-60"
            options={recipientOptions({ query, users, userId: principal.userId, current: current === '' ? '' : `user:${current}` })}
            onChoose={(selector) => {
              setOpen(false);
              edit('assigneeId', selector.slice(selector.indexOf(':') + 1));
              flush();
            }} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * A row opened for editing on the card (S5 §8.3): its kind's fields for its op, each with the app's editor, sent as
 * the person types (`useRowWriter`). An update row may become a new record ("Tạo mới", when the unique rule allows)
 * or point at another record of its kind ("Đổi sang bản ghi khác"); a new row may instead update one that exists.
 * The kind never changes: a wrong kind is a row to leave out.
 */
export function RowEditor({ jobId, row, value, edit, flush, send, error, writing }: {
  jobId: Id;
  row: BatchRowDTO;
  value: (field: string) => unknown;
  edit: (field: string, value: unknown) => void;
  flush: () => void;
  send: (change: { op?: 'create' | 'update'; targetId?: Id }) => void;
  error: RowError | null;
  writing: boolean;
}) {
  const fields = FIELD_SUBSETS[row.kind][row.op] ?? [];
  const canCreate = FIELD_SUBSETS[row.kind].create !== undefined;
  const canUpdate = FIELD_SUBSETS[row.kind].update !== undefined;
  const opError = error !== null && (error.field === 'op' || error.field === 'targetId') ? error.message : null;
  return (
    <div data-row-editor="" onBlur={flush}
      className="mt-1.5 flex flex-col gap-2.5 rounded-lg bg-[hsl(var(--h-muted)/0.45)] p-2.5">
      {canCreate && canUpdate && (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <div role="group" aria-label="Cách ghi" className="inline-flex rounded-lg bg-[hsl(var(--h-muted))] p-0.5">
              <button type="button" aria-pressed={row.op === 'create'} disabled={writing || row.op === 'create'}
                onClick={() => send({ op: 'create' })}
                className={cn(SEGMENT, row.op === 'create' ? SEGMENT_ON : SEGMENT_OFF)}>Tạo mới</button>
              {row.op === 'update' ? (
                <button type="button" aria-pressed="true" disabled className={cn(SEGMENT, SEGMENT_ON)}>Cập nhật</button>
              ) : (
                <RefPicker jobId={jobId} from={{ rowId: row.id, of: 'target' }} label="Cập nhật bản ghi có sẵn" disabled={writing}
                  onPick={(id) => send({ op: 'update', targetId: id })}
                  className={cn(SEGMENT, SEGMENT_OFF, 'font-semibold no-underline')}>Cập nhật</RefPicker>
              )}
            </div>
            {row.op === 'update' && (
              <RefPicker jobId={jobId} from={{ rowId: row.id, of: 'target' }} label="Đổi sang bản ghi khác" disabled={writing}
                onPick={(id) => send({ targetId: id })} className="text-xs">Đổi sang bản ghi khác</RefPicker>
            )}
          </div>
          {opError !== null && <p role="alert" className={ERROR}>{opError}</p>}
        </div>
      )}
      {fields.map((f) => (
        <Field key={f} field={f} editor={EDITOR_OF[f] ?? { type: 'text' }} value={value(f)} edit={edit} flush={flush} />
      ))}
      {row.kind === 'task' && row.op === 'create' && <AssigneeField value={value('assigneeId')} edit={edit} flush={flush} />}
    </div>
  );
}
